import cookies from 'js-cookie'
import { get, memoize } from 'lodash'
import * as Sentry from '@sentry/browser'
import { getSafeLocalStorage, getSafeSessionStorage } from 'src/helpers/browser'
import { getClientIdFromPath } from 'src/constants/navigation'
import { AUTH } from 'src/constants'

const localStorage = getSafeLocalStorage()
const sessionStorage = getSafeSessionStorage()

/**
 * PREFER_CLIENT_AUTH
 * Used to determine whether or not to use client auth instead of agent
 * auth for the current session.
 *
 * Goal is to prefer the client JWT over the agent JWT when a user comes
 * in through the auth routes. Auth routes will automatically set the client JWT
 * from jwt in fragment, so when the client JWT gets set, we flag the entire
 * session
 */
const PREFER_CLIENT_AUTH = 'PREFER_CLIENT_AUTH'

const getPreferClientFlag = () =>
  sessionStorage.getItem(PREFER_CLIENT_AUTH) === 'true'

const setPreferClientFlag = () =>
  sessionStorage.setItem(PREFER_CLIENT_AUTH, 'true')

const getCustomerToken = () => {
  const cookie = cookies.getJSON(AUTH.SESSION_COOKIE)
  return get(cookie, AUTH.COOKIE_TOKEN_KEY)
}

const getClientToken = () => localStorage.getItem(AUTH.ACCESS_TOKEN_KEY)

export const hasClientToken = () => Boolean(getClientToken())
export const hasCustomerToken = () => Boolean(getCustomerToken())

export const getClientIdFromToken = (token = getClientToken()) =>
  get(parseJWT(token), 'acr.values[0].context', '').replace(/client:/, '') ||
  null

const getBearerToken = () => {
  if (getPreferClientFlag()) {
    const clientToken = getClientToken()
    const tokenId = getClientIdFromToken(clientToken)
    const pathId = getClientIdFromPath()

    return clientToken && (tokenId === pathId || !pathId)
      ? clientToken
      : getCustomerToken()
  }
  return getCustomerToken() || getClientToken()
}

export const parseJWT = memoize(token => {
  const content = (token || '').split('.')[1]

  if (!content) {
    return undefined
  }

  try {
    const jwtContentRaw = atob(content)
    return JSON.parse(jwtContentRaw)
  } catch (e) {
    Sentry.captureException(e, {
      message: 'try JSON.parse(atob(content))',
      content,
      token
    })
    return undefined
  }
})

export const getParsedJWT = () => parseJWT(getBearerToken())

export const getAuthEmail = () => (getParsedJWT() || {}).email

export const clearClientToken = () => {
  localStorage.removeItem(AUTH.ACCESS_TOKEN_KEY)
}

export const setClientToken = token => {
  localStorage.setItem(AUTH.ACCESS_TOKEN_KEY, token)
  setPreferClientFlag()
}

export const getAuthHeaders = () => {
  const token = getBearerToken()
  const headers = {}

  if (token) {
    headers.Authorization = `Bearer ${getBearerToken()}`
  }

  return headers
}

export const isAuthenticated = () => Boolean(getBearerToken())
